/**
 * Le retour d'une action réussie — décidé UNE fois, pour toutes les mutations.
 *
 * CE QUE CELA CORRIGE
 * -------------------
 * L'application comptait quatre `toast` pour soixante-quatorze mutations. Créer un sprint, une
 * story, un commentaire ou un lien ne produisait AUCUNE confirmation : `onSuccess` rechargeait
 * la liste, et c'était tout. L'infrastructure existait pourtant — `sonner` installé, `<Toaster/>`
 * monté dans `AppShell` — elle ne servait pas.
 *
 * POURQUOI UN CACHE GLOBAL PLUTÔT QUE 74 `onSuccess`
 * --------------------------------------------------
 * Passer sur chaque site d'appel aurait coûté 74 modifications pour un résultat qui aurait
 * dérivé dès la 75e mutation écrite. Le `MutationCache` observe TOUTES les mutations, y compris
 * celles écrites demain. Le comportement par défaut est ainsi acquis sans toucher un écran, et
 * `meta` sert à l'affiner là où le défaut ne convient pas.
 *
 * L'ASYMÉTRIE DES DÉFAUTS EST DÉLIBÉRÉE
 * -------------------------------------
 * - Succès → toast par défaut. Il n'existe RIEN aujourd'hui : aucun risque de doublon, et le
 *   gain porte immédiatement sur la totalité des mutations.
 * - Erreur → PAS de toast par défaut. 103 `<ErrorState>` rendent déjà le message du serveur à
 *   l'endroit où l'utilisateur regarde. Doubler par défaut créerait 103 doublons pour corriger
 *   la dizaine de cas où l'écran d'origine a disparu — d'où `erreurToast` explicite.
 *
 * LA RÈGLE DE DÉCISION (`retour`)
 * -------------------------------
 * Une seule question, à laquelle le code répond : **la surface qui a déclenché l'action
 * survit-elle pour montrer le résultat ?** Un `setOpen(false)`, un `navigate()` ou une ligne
 * supprimée répondent non → `toast`. Si l'effet est visible sur place en moins d'un écran de
 * défilement → `insitu`, et c'est alors `useAnnonce()` qui porte l'information aux lecteurs
 * d'écran, pour qui « visible » ne veut pas dire « perceptible ».
 */
import type { MouseEvent } from 'react'
import { MutationCache } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { ApiError } from '@/api/client'
import { langueCourante, traduire } from '@/i18n/language'

/**
 * Un ALIAS de type, pas une interface : `MutationMeta` de TanStack n'accepte l'augmentation que
 * si le type satisfait `Record<string, unknown>`, ce qu'une interface ne fait pas (elle n'a pas
 * d'index signature implicite). Avec une interface, la contrainte échoue en silence et `meta`
 * retombe sur son type générique — le typage promis ici serait alors une illusion.
 */
export type MetaMutation = {
  /** `'toast'` (défaut) quand le déclencheur disparaît ; `'insitu'` quand l'effet est visible. */
  retour?: 'toast' | 'insitu'
  /** Phrase de succès NOMMANT l'objet — « Sprint créé », pas « Enregistré ». */
  succes?: string
  detail?: string
  /** Remonter l'échec en toast : indispensable quand l'écran d'origine n'est plus là. */
  erreurToast?: boolean
  /**
   * Action de suivi (« Voir le résultat ») pour les traitements différés. La signature de
   * `onClick` est celle de `sonner` : elle reçoit l'événement du bouton.
   */
  action?: { label: string; onClick: (event: MouseEvent<HTMLButtonElement>) => void }
}

// Type `meta` sur TOUTES les mutations du dépôt : une faute de frappe (`retur`) cesse de
// compiler au lieu de désactiver silencieusement le retour.
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: MetaMutation
  }
}

export const mutationCache = new MutationCache({
  onSuccess: (_donnees, _variables, _contexte, mutation) => {
    const meta = mutation.meta
    // `insitu` : l'écran montre déjà le résultat, un toast ferait doublon avec ce que
    // l'utilisateur a sous les yeux.
    if (meta?.retour === 'insitu') return

    toast.success(
      meta?.succes ?? traduire(langueCourante(), 'mutation.ok', 'Modification enregistrée'),
      {
        // Un identifiant stable par mutation : re-cliquer remplace le toast au lieu d'en empiler
        // un second, ce qui arrivait sur les boutons à double clic facile.
        id: `mut-${mutation.mutationId}`,
        // `exactOptionalPropertyTypes` est actif : ces clés facultatives sont OMISES quand elles
        // sont absentes, jamais passées à `undefined`.
        ...(meta?.detail ? { description: meta.detail } : {}),
        ...(meta?.action ? { action: meta.action } : {}),
      },
    )
  },

  onError: (erreur, _variables, _contexte, mutation) => {
    if (!mutation.meta?.erreurToast) return
    const e = erreur as ApiError
    toast.error(e.message, { id: `mut-${mutation.mutationId}`, description: e.detail })
  },
})
